<style lang="postcss">
	.table-entry-cover {
		width: 100%;
	}
	.table-entry {
		display: flex;
		flex-direction: column;
		width: 100%;
		overflow-x: auto;
	}
	.table-entry-grid {
		border-width: 1px;
		border-style: solid;
		width: fit-content;
		display: grid;
		min-width: 100%;
	}
	.cell,
	:global(.table-body-cell-cover .cell) {
		border-style: solid;
		padding: 0.4rem 0.6rem;
		background-color: transparent;
		white-space: nowrap;
		font-size: 0.875rem;
		line-height: 1.25rem;
	}
	.header-cell {
		font-weight: 500;
	}
	.table-body-cell-cover {
		display: flex;
		align-items: center;
		width: 100%;
		border-top-width: 1px;
		position: relative;
	}
	.table-datepicker-cover {
		display: flex;
		flex-flow: column;
		align-items: center;
		width: 100%;
	}
	select.cell {
		cursor: pointer;
	}
	.cell,
	select.cell {
		border-radius: 1px;
		box-shadow: 0px 0px 0px 1px transparent;
	}
	select.cell {
		padding-right: 1.5rem;
	}
	.input-cell,
	:global(.table-body-cell-cover .input-cell) {
		appearance: none;
		width: 100%;
		border-radius: 1px;
		box-shadow: none;
	}
	.input-cell:focus {
		z-index: 10;
	}
	.input-cell:hover {
		z-index: 10;
	}
</style>

<script lang="ts">
	import { numericInput } from "$lib/actions/numericInput";
	import { slide } from "$lib/transitions/slide";
	import { onTab } from "$lib/actions/onTab";
	import { onMount } from "svelte";
	import DatepickerInput from "$basic/DatepickerInput.svelte";

	export let element: HTMLDivElement = {} as HTMLDivElement;
	export let add_row_text: string = "Add Row";
	export let hide_add_row: boolean = false;
	export let rows: string = "3";
	export let max: string = "";
	export let headers: TableEntryField["settings"]["columns"];
	export let value: TableEntryValue = {
		headers: headers ? headers.map((c) => c.label) : [],
		rows: []
	};

	let extraRows = 0;
	$: max_rows = max && /^\d+$/.test(max) ? parseInt(max, 10) : null;

	$: {
		extraRows;
		const totalRows = Array.from(
			{ length: Math.min(100, parseInt(rows, 10)) + extraRows },
			(_, i) => i
		);
		totalRows.forEach((_, i) => {
			if (!value.rows[i]) {
				value.rows[i] = [
					...Array.from({ length: headers ? headers.length : 0 }, () => "")
				];
			}
		});
		let newRows = value.rows.slice(0, totalRows.length);
		if (newRows.length !== value.rows.length) {
			value.rows = [...newRows];
		}
	}

	const removeRow = (i: number, focus = false) => {
		value.rows.splice(i, 1);
		value.rows = [...value.rows];
		extraRows--;
		if (!focus) return;
		requestAnimationFrame(() => {
			const first_input = element.querySelector(
				`[data-row-index='${i - 1}'] input:first-of-type`
			);
			if (first_input instanceof HTMLInputElement) first_input.focus();
		});
	};

	onMount(() => {
		if (typeof value === "undefined" || value === null) {
			value = {
				headers: headers ? headers.map((c) => c.label) : [],
				rows: []
			};
		}
	});
</script>

<div class="table-entry-cover" bind:this="{element}">
	<div class="table-entry">
		<div
			style="grid-template-columns: repeat({headers.length}, minmax(min-content, 1fr))"
			class="table-entry-grid">
			{#each headers as column, columnIndex}
				<div
					style="border-right-width: {columnIndex !== headers.length - 1
						? '1px'
						: '0px'}"
					class="cell header-cell table-entry-border">
					{column.label}
				</div>
			{/each}
			{#each value.rows as _, i (i)}
				{#each headers as column, columnIndex}
					<div
						transition:slide
						style="border-right-width: {columnIndex !== headers.length - 1
							? '1px'
							: '0px'}"
						data-row-index="{i}"
						class="table-body-cell-cover table-entry-border">
						{#if column.type === "dropdown" && typeof column.options === "object"}
							<select
								bind:value="{value.rows[i][columnIndex]}"
								title="{column.label}"
								aria-label="{column.label}"
								class="cell input-cell">
								<option disabled hidden selected value=""></option>
								{#each column.options as option}
									<option value="{option}">{option}</option>
								{/each}
							</select>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								width="10"
								height="10"
								style="position: absolute; pointer-events: none; right: 9px"
								viewBox="0 0 24 24"
								stroke-width="2.25"
								stroke="currentColor">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									d="M19.5 8.25l-7.5 7.5-7.5-7.5"></path>
							</svg>
						{:else if column.type === "date"}
							{#key column.dateFormat}
								<DatepickerInput
									bind:value="{value.rows[i][columnIndex]}"
									label="{column.label}"
									format="{column.dateFormat}"
									class="cell input-cell" />
							{/key}
						{:else}
							<input
								aria-label="{column.label}"
								data-1p-ignore
								type="text"
								use:numericInput="{{
									disabled: column.type !== 'numeric'
								}}"
								use:onTab="{{
									keys: ['Backspace', 'Tab'],
									callbackFunction: (key) => {
										if (
											key === 'Backspace' &&
											columnIndex === 0 &&
											value.rows[i][columnIndex] === ''
										) {
											requestAnimationFrame(() => {
												removeRow(i, true);
											});
										}
										if (
											key === 'Tab' &&
											columnIndex === headers.length - 1 &&
											i === value.rows.length - 1
										) {
											extraRows += 1;
										}
									}
								}}"
								bind:value="{value.rows[i][columnIndex]}"
								class="cell input-cell" />
						{/if}
					</div>
				{/each}
			{/each}
		</div>
	</div>
	<button
		style="margin-top: .5rem"
		type="button"
		class:hidden="{hide_add_row}"
		disabled="{max_rows && value.rows.length >= max_rows}"
		class="button sm"
		on:click="{() => {
			if (max_rows && value.rows.length >= max_rows) {
				return;
			} else {
				extraRows += 1;
			}
		}}">
		{add_row_text}
	</button>
</div>
